import { useMemo } from 'react'

import { Alert, Flex, Spinner } from '@vtex/shoreline'

import BadgeIsValid from '~/components/BadgeIsValid'
import Panel from '~/components/Panel'
import { Badge } from '~/components/UI/Badge'
import { FormGroup, InputMoney, Label } from '~/components/unform'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import LoadingValue from '~/modules/retailMedia/components/CampaignForm/elements/Investment/LoadingValue'

import { useEditableTotalBudget } from '../hooks'

const TotalBudgetForm: BudgetComponent = props => {
  const { t } = useAPPTranslation()
  const { Title, values, isLoading, isMinMaxBudgetLoading, hasToFillFields } =
    props

  const {
    handleChange,
    hasNoInventory,
    isInputDisabled,
    showBadgeIsInvalid,
    showBadgeIsValid,
    sectionIsValid,
    minTotalBudgetLabel,
    maxTotalBudgetLabel,
    shouldShowMinMaxLabel,
    totalBudget,
    BudgetContent,
    FIELD_NAME
  } = useEditableTotalBudget(props)

  const iconLeft = useMemo(
    () =>
      isLoading ? (
        <Spinner size={20} />
      ) : (
        <BadgeIsValid isValid={sectionIsValid} />
      ),
    [isLoading, sectionIsValid]
  )

  const hasNoInventoryAlert = useMemo(
    () =>
      hasNoInventory ? (
        <Alert variant="critical" style={{ marginBottom: '1rem' }}>
          {t('rm:campaign.form.alerts.noInventory')}
        </Alert>
      ) : null,
    [hasNoInventory, t]
  )

  return (
    <Panel
      data-investment-budget="totalBudget-form"
      iconLeft={iconLeft}
      title={Title}
      description={t(
        'rm:campaign.create.Investment.editableTotalBudget.description'
      )}
    >
      {hasToFillFields && (
        <Alert variant="warning" style={{ marginBottom: '1rem' }}>
          {t('rm:campaign.form.alerts.fillAllFields')}
        </Alert>
      )}

      {hasNoInventoryAlert}

      <Flex align="end">
        <FormGroup style={{ flex: 1, maxWidth: '25%', minWidth: '20rem' }}>
          <Label
            isRequired
            htmlFor={FIELD_NAME}
            helperText={
              <LoadingValue isLoading={isMinMaxBudgetLoading}>
                {!!shouldShowMinMaxLabel &&
                  t(
                    'rm:campaign.create.Investment.editableTotalBudget.helperText',
                    {
                      minTotalBudgetLabel,
                      maxTotalBudgetLabel
                    }
                  )}
              </LoadingValue>
            }
          >
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>
                {t('rm:totalBudget')}
              </span>
            </div>
          </Label>
          <InputMoney
            required
            id={FIELD_NAME}
            name={FIELD_NAME}
            onChange={e => handleChange(e)}
            disabled={isInputDisabled}
            showBadgeIsInvalid={showBadgeIsInvalid}
            showBadgeIsValid={showBadgeIsValid}
            placeholder={t(
              'rm:campaign.create.Investment.editableTotalBudget.input.placeholder'
            )}
          />
        </FormGroup>
      </Flex>

      <section
        className="d-flex gap-3"
        style={{
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: 'var(--sl-border-base)',
          minHeight: '4.5rem',
          flexWrap: 'wrap'
        }}
      >
        <div className="pe-3 border-end">
          <Label text={t('rm:campaign.create.Investment.duration.title')} />
          <span>
            {values.duration ? (
              t('rm:campaign.create.Investment.duration', {
                duration: values.duration
              })
            ) : (
              <Badge template="warning">
                {t('rm:campaign.create.Investment.duration.placeholder')}
              </Badge>
            )}
          </span>
        </div>

        <div className="span-start pe-3 border-end">
          <Label text={t('rm:campaign.create.Investment.places.title')} />
          <span>
            {values.totalPlaces || (
              <Badge template="warning">
                {t('rm:campaign.create.Investment.places.placeholder')}
              </Badge>
            )}
          </span>
        </div>

        <div className="span-start pe-3 border-end">
          <Label text={t('rm:campaign.create.Investment.totalCost.title')} />
          <span>
            {totalBudget?.formattedValue || (
              <Badge template="warning">
                {t('rm:campaign.create.Investment.totalCost.placeholder')}
              </Badge>
            )}
          </span>
        </div>

        <div>
          <Label
            text={t(
              'rm:campaign.create.Investment.purchaseReproductions.title'
            )}
          />
          <span>
            <LoadingValue isLoading={isLoading}>{BudgetContent}</LoadingValue>
          </span>
        </div>
      </section>
    </Panel>
  )
}

export default TotalBudgetForm
